import { apiClient } from "../../../client";
import { StandardAxiosConfig, getCompressed, Filter, getBaseDataURL, parseDates } from "../../../common";
import { BaseDataModel } from "@domain/CRM/BaseDataModel";
export type ModelConstructor<T> = new () => T;

export class GetAPI_Base<T extends BaseDataModel> {
	protected baseUrl: string;
	private isCompressed: boolean = false;
	private ModelClass: ModelConstructor<T>;

	constructor(router: string, ModelClass: ModelConstructor<T>, isCompressed: boolean = false) {
		this.baseUrl = `${getBaseDataURL(router)}/get`;
		this.ModelClass = ModelClass;
		this.isCompressed = isCompressed;
	}

	private hydrateRecord(record: T): T {
		return Object.assign(new this.ModelClass(), record);
	}

	private hydrateRecords(records: T[]): T[] {
		return records.map((r) => Object.assign(new this.ModelClass(), r));
	}

	/** Builds the `?compressed=true&fresh=1`-style query string shared by `byFilter` and
	 *  `byId` (each has its own `isCompressed` — `byId` can override the instance default). */
	private queryString(isCompressed: boolean, fresh?: boolean): string {
		const queryParams: string[] = [];
		if (isCompressed) queryParams.push(`compressed=true`);
		if (fresh) queryParams.push(`fresh=1`);
		return queryParams.length ? `?${queryParams.join("&")}` : "";
	}

	public async all(fresh?: boolean): Promise<T[]> {
		return this.byFilter(undefined, fresh);
	}

	/** The optional `fresh` flag bypasses the server-side read cache for this request while
	 *  still repopulating it (`?fresh=1`) — see `useFreshOnEntry`. */
	public async byFilter(filter: Filter<T> = { fields: [] } as Filter<T>, fresh?: boolean): Promise<T[]> {
		const url = `${this.baseUrl}${this.queryString(this.isCompressed, fresh)}`;

		if (this.isCompressed) {
			const records = (await getCompressed(url, filter)) as T[];
			parseDates<T>(records);
			return this.hydrateRecords(records);
		} else {
			const response = await apiClient.post(url, filter, StandardAxiosConfig());
			const records = response.data as T[];
			parseDates<T>(records);
			return this.hydrateRecords(records);
		}
	}

	public async byIds(ids: string[], fresh?: boolean): Promise<T[]> {
		const filter = {
			type: "OR",
			fields: ids.map((id) => ({ id: id })),
		} as Filter<T>;

		const records = await this.byFilter(filter, fresh);
		parseDates<T>(records);
		return records;
	}

	public async byId(id: string, isCompressed: boolean = this.isCompressed, fresh?: boolean): Promise<T> {
		const url = `${this.baseUrl}/${id}${this.queryString(isCompressed, fresh)}`;

		if (isCompressed) {
			const record = await getCompressed(url);
			if (Array.isArray(record)) {
				const r = record[0] as T;
				parseDates<T>([r]);
				return this.hydrateRecord(r);
			} else {
				const r = record as T;
				parseDates<T>([r]);
				return this.hydrateRecord(r);
			}
		} else {
			const response = await apiClient.get(url, StandardAxiosConfig());
			const record = response.data as T;
			parseDates<T>([record]);
			return this.hydrateRecord(record);
		}
	}
}
